import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, CheckCircle2, Circle, Clock, Lock, Award, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

const LEVEL_LABELS = { membros: "Membros", coordenadores: "Coordenadores", direcao: "Direção" };

export default function CourseDetail() {
  const { t, lang } = useI18n();
  const { id } = useParams();
  const { user } = useAuth();
  const [course, setCourse] = useState(null);
  const [done, setDone] = useState([]);
  const [needLogin, setNeedLogin] = useState(false);
  const [quiz, setQuiz] = useState({}); // { moduleId: {selected, checked} }

  useEffect(() => {
    if (user === false) { setNeedLogin(true); return; }
    if (!user) return;
    api.get(`/courses/${id}`, { params: { lang } }).then((r) => setCourse(r.data)).catch((e) => {
      if (e.response?.status === 401) setNeedLogin(true);
    });
    api.get("/me/courses/progress").then((r) => setDone(r.data[id] || [])).catch(() => {});
  }, [id, user, lang]);

  const toggle = async (moduleId, completed) => {
    const { data } = await api.post(`/me/courses/${id}/module`, { module_id: moduleId, completed });
    setDone(data.completed_module_ids);
  };
  const checkQuiz = (m) => {
    const sel = quiz[m.id]?.selected;
    const correct = sel === m.quiz.answer_index;
    setQuiz((q) => ({ ...q, [m.id]: { selected: sel, checked: true, correct } }));
  };

  if (needLogin) {
    return (
      <div className="max-w-lg mx-auto px-4 py-24 text-center">
        <div className="w-14 h-14 rounded-full bg-th-blue/10 flex items-center justify-center mx-auto"><Lock className="w-6 h-6 text-th-blue" /></div>
        <h1 className="mt-4 font-display font-semibold text-2xl text-th-blue">Formação reservada a membros</h1>
        <p className="mt-2 text-slate-500">Entre com a sua conta para aceder aos conteúdos da Escola.</p>
        <Button asChild className="mt-6 bg-th-blue text-white hover:bg-th-bluelight" data-testid="course-login-btn"><Link to="/entrar">{t("nav_login")}</Link></Button>
      </div>
    );
  }
  if (!course) return <div className="max-w-4xl mx-auto px-4 py-20 text-slate-500">{t("loading")}</div>;

  const locked = course.locked;
  const total = course.modules?.length || 0;
  const pct = total ? Math.round((done.length / total) * 100) : 0;
  const allDone = total > 0 && course.modules.every((m) => done.includes(m.id));

  return (
    <div>
      <section className="relative bg-th-blue text-white overflow-hidden">
        <div className="absolute inset-0"><img src={course.image_url} alt="" className="w-full h-full object-cover opacity-25" /><div className="absolute inset-0 bg-th-blue/85" /></div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <Link to="/escola" className="inline-flex items-center gap-1 text-white/70 hover:text-th-gold text-sm mb-5" data-testid="back-school"><ArrowLeft className="w-4 h-4" />{t("nav_school")}</Link>
          <Badge className="bg-th-gold text-th-bluedark mb-3">{LEVEL_LABELS[course.access_level] || course.level}</Badge>
          <h1 className="font-display font-bold text-3xl sm:text-4xl">{course.title}</h1>
          <p className="mt-3 text-white/75 max-w-2xl">{course.description}</p>
          {!locked && (
            <div className="mt-5 max-w-sm">
              <div className="flex justify-between text-sm text-white/70 mb-1"><span>{t("school_progress")}</span><span>{done.length}/{total}</span></div>
              <Progress value={pct} className="h-2 bg-white/20" />
            </div>
          )}
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {locked ? (
          <div className="text-center bg-white rounded-2xl border border-slate-200 p-10 shadow-sm" data-testid="course-locked">
            <div className="w-14 h-14 rounded-full bg-th-blue/10 flex items-center justify-center mx-auto"><Lock className="w-6 h-6 text-th-blue" /></div>
            <h2 className="mt-4 font-display font-semibold text-xl text-th-blue">Formação reservada a {LEVEL_LABELS[course.access_level]}</h2>
            <p className="mt-2 text-slate-500 max-w-md mx-auto">O conteúdo deste curso está disponível para o nível <strong>{LEVEL_LABELS[course.access_level]}</strong>. Se acha que devia ter acesso, fale com a coordenação para que atualizem o seu perfil.</p>
            <div className="mt-6 text-left max-w-md mx-auto space-y-2">
              <p className="text-xs uppercase tracking-wide text-slate-400 font-semibold">Módulos deste curso</p>
              {course.modules?.map((m, i) => (
                <div key={m.id} className="flex items-center gap-2 text-slate-500 text-sm"><Lock className="w-3.5 h-3.5 text-slate-300 shrink-0" />{i + 1}. {m.title}{m.duration && <span className="text-slate-300">· {m.duration}</span>}</div>
              ))}
            </div>
          </div>
        ) : (
          <>
            {allDone && (
              <div className="mb-6 rounded-2xl border border-th-gold/40 bg-th-gold/10 p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-testid="certificate-banner">
                <div className="flex items-center gap-3">
                  <Award className="w-8 h-8 text-th-gold shrink-0" />
                  <div><div className="font-display font-semibold text-th-blue">Curso concluído!</div><div className="text-sm text-slate-500">Já pode emitir o seu certificado de conclusão.</div></div>
                </div>
                <Button asChild className="bg-th-blue text-white hover:bg-th-bluelight" data-testid="get-certificate-btn"><Link to={`/escola/${id}/certificado`}>Ver certificado</Link></Button>
              </div>
            )}
            <Accordion type="single" collapsible className="space-y-3" data-testid="course-modules">
              {course.modules?.map((m, i) => {
                const isDone = done.includes(m.id);
                const qs = quiz[m.id] || {};
                const hasQuiz = !!m.quiz;
                const passed = !hasQuiz || (qs.checked && qs.correct);
                return (
                  <AccordionItem key={m.id} value={m.id} data-testid={`module-${m.id}`} className="bg-white rounded-xl border border-slate-200 px-5">
                    <AccordionTrigger className="hover:no-underline">
                      <div className="flex items-center gap-3 text-left">
                        {isDone ? <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0" /> : <Circle className="w-5 h-5 text-slate-300 shrink-0" />}
                        <div>
                          <div className="font-display font-medium text-th-blue">{i + 1}. {m.title}</div>
                          {m.duration && <div className="text-xs text-slate-400 flex items-center gap-1 mt-0.5"><Clock className="w-3 h-3" />{m.duration}</div>}
                        </div>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>
                      <p className="text-slate-600 leading-relaxed pb-3 whitespace-pre-line">{m.content}</p>
                      {hasQuiz && (
                        <div className="mb-4 rounded-xl bg-slate-50 border border-slate-200 p-4" data-testid={`quiz-${m.id}`}>
                          <div className="font-medium text-th-blue text-sm mb-3">{t("quiz_question")} {m.quiz.question}</div>
                          <div className="space-y-2">
                            {m.quiz.options.map((opt, oi) => {
                              const selected = qs.selected === oi;
                              const showResult = qs.checked;
                              const isAnswer = oi === m.quiz.answer_index;
                              return (
                                <button key={oi} type="button" data-testid={`quiz-${m.id}-opt-${oi}`}
                                  onClick={() => setQuiz((q) => ({ ...q, [m.id]: { selected: oi, checked: false } }))}
                                  className={`w-full text-left px-3 py-2 rounded-lg border text-sm transition-colors ${
                                    showResult && isAnswer ? "border-green-500 bg-green-50 text-green-700"
                                    : showResult && selected && !isAnswer ? "border-red-400 bg-red-50 text-red-600"
                                    : selected ? "border-th-blue bg-th-blue/5 text-th-blue" : "border-slate-200 hover:border-th-blue/40"}`}>
                                  {opt}
                                </button>
                              );
                            })}
                          </div>
                          <div className="mt-3 flex items-center gap-3">
                            <Button size="sm" variant="outline" className="border-slate-300 text-th-blue" disabled={qs.selected == null} onClick={() => checkQuiz(m)} data-testid={`quiz-check-${m.id}`}>{t("quiz_check")}</Button>
                            {qs.checked && (qs.correct
                              ? <span className="text-green-600 text-sm flex items-center gap-1" data-testid={`quiz-result-${m.id}`}><CheckCircle2 className="w-4 h-4" />{t("quiz_correct")}</span>
                              : <span className="text-red-500 text-sm flex items-center gap-1" data-testid={`quiz-result-${m.id}`}><XCircle className="w-4 h-4" />{t("quiz_retry")}</span>)}
                          </div>
                        </div>
                      )}
                      <Button size="sm" onClick={() => toggle(m.id, !isDone)} disabled={!isDone && !passed} data-testid={`module-toggle-${m.id}`}
                        className={isDone ? "bg-slate-100 text-slate-600 hover:bg-slate-200" : "bg-th-blue text-white hover:bg-th-bluelight"}>
                        {isDone ? <><CheckCircle2 className="w-4 h-4 mr-1" />{t("module_complete")}</> : t("module_mark")}
                      </Button>
                      {hasQuiz && !isDone && !passed && <p className="text-xs text-slate-400 mt-2">{t("quiz_hint")}</p>}
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>
          </>
        )}
      </section>
    </div>
  );
}
